<script lang="ts">
    import { ClusterService } from "$/services/ClusterService";
    import { ClusterStore, ClusterType } from "$/stores/ClusterStore";
    import { TenantStore } from "$/stores/TenantStore";
    import { ActiveTopic } from "$/stores/extension/ActiveTopicStore";
    import { clickAction } from "$/utils/clickAction";
    import { onMount } from "svelte";
    import SVGIcon from "../../SVGIcon.svelte";
    import type { Cluster, Tenant } from "./types";
    import { tooltip } from "$/utils/tooltip";
    import { getEffectivePricingPlan, showtToastMessage } from "$/utils/Utils";

    export let showMoreOptionsMenu = false;
    export let tenant: Tenant;
    export let copyComments: boolean;

    const forbiddenClusters = [
        ClusterType.ARCHIVED,
        ClusterType.SHARED,
        ClusterType.SHARED_WITH_ME,
        ClusterType.RESTRICTED,
    ];
    let tenantStoreId = $TenantStore?.active.id;
    let isActiveTenant = tenant.id === tenantStoreId;
    let dropdownItemOpen = isActiveTenant;
    let licenseExpired = getEffectivePricingPlan(tenant) === -1;
    let clusters: Cluster[] = [];

    function toggleDropdownItemOpen() {
        dropdownItemOpen = !dropdownItemOpen;
    }

    function handleClick(clusterId: string) {
        showtToastMessage("Copying topic. This might take a while...");
        ClusterStore.copyTopic(
            tenant.id,
            tenantStoreId,
            clusterId,
            $ActiveTopic.id,
            copyComments,
        );
        showMoreOptionsMenu = false;
    }

    function filterForbiddenClusters(clusters: Cluster[]) {
        return clusters.filter(
            (cluster) => !forbiddenClusters.includes(cluster.clusterType),
        );
    }

    onMount(async () => {
        if (isActiveTenant) {
            clusters = $ClusterStore.clusters;
        } else {
            clusters = await ClusterService.getClustersByTenantId(tenant.id);
            ClusterStore.sortClusters(clusters);
        }
        clusters = filterForbiddenClusters(clusters);
    });
</script>

<!-- ACCORDION HEADER -->
<div
    class="flex select-none flex-row items-center gap-1 rounded py-1 pl-1 pr-4 font-bold hover:bg-gray-200"
>
    <div
        class="grid h-6 w-6 place-items-center rounded hover:bg-gray-300"
        use:clickAction={toggleDropdownItemOpen}
    >
        <div
            class="translate-x-[1px] rotate-90 transition-all"
            class:open={!dropdownItemOpen}
        >
            <SVGIcon name="carret" size={16} height={16} strokeWidth="1.5" />
        </div>
    </div>
    <span>{tenant.name}</span>
</div>

<!-- ACCORDION CHILD ITEMS -->
{#if dropdownItemOpen}
    {#if clusters.length > 0}
        {#each clusters as cluster}
            <button
                class="flex w-full items-center gap-1 overflow-hidden rounded pl-7 pr-4 hover:bg-gray-200"
                on:click={() => handleClick(cluster.id)}
                disabled={licenseExpired}
                use:tooltip={licenseExpired ? "Subscription expired" : ""}
                class:expired={licenseExpired}
            >
                <div
                    class="relative self-baseline rounded p-1 hover:bg-gray-200"
                >
                    {#if cluster.clusterType === ClusterType.TENANT_DEFAULT}
                        {#if tenant.personal}
                            <SVGIcon name="user" size={20} strokeWidth="1.5" />
                        {:else}
                            <SVGIcon
                                name="building"
                                size={20}
                                strokeWidth="1.5"
                            />
                        {/if}
                    {:else}
                        <SVGIcon name="board" size={20} strokeWidth="1.5" />
                    {/if}
                </div>
                <span class="truncate">{cluster.name}</span>
            </button>
        {/each}
    {:else}
        <div class="p-1 pl-8">Loading...</div>
    {/if}
{/if}

<style>
    .open {
        @apply rotate-0;
    }

    .expired {
        @apply cursor-not-allowed opacity-50;
    }
</style>
